<template>
  <span ref="wrapperEl" class="main-wrapper" @mouseenter="showTooltip" @mouseleave="hideTooltip" @click="clickTitle()">
    <CitationInfoLabel
      v-if="display2ndOrderReferences"
      :label="displayLabel"
      :source="source"
      :show-icon="!isOverflow"
    />
    <span v-else class="info-icon-black">{{ displayLabel }}</span>

    <InfoTooltipContent
      v-if="isTooltipVisible"
      :id="id"
      :title="title"
      :page="page"
      :snippet="snippet"
      :thumb-url="thumbUrl"
      :source="source"
      :reference="currentReference"
      :overflow-references="overflowReferences"
      @title-clicked="clickTitle"
      @preview="clickPreview"
      class="tooltip-container"
      :style="tooltipStyle"
    />
  </span>
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { decodeFromBase64 } from '~/utils/message';
import CitationInfoLabel from '~/components/conversation/chatMessage/CitationInfoLabel.vue';
import InfoTooltipContent from '~/components/conversation/chatMessage/InfoTooltipContent.vue';
import useConversations from '~/composables/useConversations';

export default {
  name: 'InfoTooltip',
  components: { CitationInfoLabel, InfoTooltipContent },
  emits: ['titleClicked'],
  props: {
    id: {
      type: String,
      default: '',
    },
    snippetIndex: {
      type: String,
      default: '',
    },
    messageData: {
      type: Object,
      default: () => {},
    },
    page: {
      type: String,
      default: '',
    },
    label: {
      type: Number,
      default: 1,
    },
    onGetTooltipThumb: {
      type: Function,
      default: () => {},
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    refNum: {
      type: Number,
      default: null,
    },
    referenceId: {
      type: String,
      default: '',
    },
    // When set, this badge represents several second-order sources grouped under one "+N"
    // badge (beyond MAX_INDIVIDUAL_BULLETS shown individually) — each entry needs its own
    // { referenceId, refNum, label } to be resolved to a full reference on hover.
    overflowEntries: {
      type: Array,
      default: () => [],
    },
  },

  setup(props, { emit }) {
    const isTooltipVisible = ref(false);
    const thumbUrl = ref('');
    const currentReference = ref({});
    const overflowReferences = ref([]);
    const wrapperEl = ref(null);
    const tooltipStyle = ref({});

    const { display2ndOrderReferences } = useConversations();
    const { t } = useI18n();

    // Computed fresh on every hover (not once at mount/message-update time, then cached) so it
    // reflects the current layout even if the conversation panel resized or scrolled since this
    // badge last re-rendered — a stale cached position was the actual cause of intermittent
    // left-edge truncation. Clamped against the scrollable conversation panel itself (not just
    // this message bubble's own width), since that's the box that actually clips overflow.
    const computeTooltipStyle = () => {
      const boundaryEl = wrapperEl.value?.closest('.scrollable-content') || wrapperEl.value?.parentElement;
      if (!wrapperEl.value || !boundaryEl) {
        tooltipStyle.value = {};
        return;
      }

      const phRect = wrapperEl.value.getBoundingClientRect();
      const containerRect = boundaryEl.getBoundingClientRect();

      const tooltipWidth = 400;
      const padding = 8;

      const desiredLeftAbs = phRect.left - containerRect.left + phRect.width / 2 - tooltipWidth / 2;
      const clampedLeftAbs = Math.max(padding, Math.min(desiredLeftAbs, containerRect.width - tooltipWidth - padding));
      const tooltipLeft = clampedLeftAbs - (phRect.left - containerRect.left);

      tooltipStyle.value = { left: `${tooltipLeft}px` };
    };

    const isOverflow = computed(() => props.overflowEntries.length > 0);
    const displayLabel = computed(() =>
      isOverflow.value ? `+${props.overflowEntries.length} ${t('common.more')}` : props.label,
    );

    /**
     * Resolves the reference/second-order-reference object to render in the tooltip for one
     * bullet. When refNum is set (a multi-source citation, one bullet per second-order source),
     * matches by referenceId alone (unique per physical occurrence). Otherwise matches this exact
     * citation (id+page+snippetIndex) against the label object's citationId, falling through to
     * each grouped passage since citations sharing a label can be page-scattered (see comments
     * below). Falls back to the bare primary reference object if no second-order data matched.
     * @param {string} referenceId - Second-order reference id (only set for multi-source citations).
     * @param {number|null} refNum - Second-order reference number (only set for multi-source citations).
     * @returns {object} A secondOrderReferences entry, or the plain reference object, or {}.
     */
    const findReferenceFor = (referenceId, refNum) => {
      const messageBlock = props.messageData?.data?.[0] || {};
      const references = Array.isArray(messageBlock.references) ? messageBlock.references : [];
      const secondOrderReferences = Array.isArray(messageBlock.secondOrderReferences)
        ? messageBlock.secondOrderReferences
        : [];
      const citationKey = `${props.id}-${props.page}-${props.snippetIndex}`;
      // Citations sharing the same doc+snippetIndex (e.g. several unsubstantiated citations on
      // different pages) collapse into one label/object, whose top-level citationId only reflects
      // one of them — so also check each grouped passage for a per-page match.
      const matchesCitation = (ref) =>
        ref.citationId === citationKey ||
        ref.citationKey === citationKey ||
        (Array.isArray(ref.passages) &&
          ref.passages.some((passage) => passage.citationKey === citationKey || passage.citationId === citationKey));
      // Second-order refs are now grouped by citation text, so the same source appearing at
      // multiple physical locations (distinct referenceId per occurrence) shares one label/object —
      // the top-level referenceId only reflects one occurrence, so also check grouped passages.
      // referenceId alone (coordinate-based) already uniquely identifies one occurrence — refNum
      // isn't required for identity (see the dedupe key above) and requiring it too here would only
      // add a way for an otherwise-correct match to fail.
      const matchesReferenceId = (ref) =>
        ref.referenceId === referenceId ||
        (Array.isArray(ref.passages) && ref.passages.some((passage) => passage.referenceId === referenceId));
      const selectedSecondOrderRef =
        refNum != null ? secondOrderReferences.find(matchesReferenceId) : secondOrderReferences.find(matchesCitation);

      if (selectedSecondOrderRef) {
        return selectedSecondOrderRef;
      }

      return references.find((ref) => ref.externalId === props.id) || {};
    };

    const findReference = () => findReferenceFor(props.referenceId, props.refNum);

    // Resolves one overflow-list entry by its own label (buildSecondOrderReferences sets
    // `index: label` on every entry, second-order or plain, and it's unique per label). Overflow
    // entries can't reliably resolve via findReferenceFor's citationKey/referenceId matching,
    // since that builds its citationKey from this component's own top-level props — fixed to
    // whichever entry happened to be first in the overflow list, not the specific entry being
    // resolved here — so a plain (no refNum/referenceId) entry would incorrectly match against an
    // unrelated reference that happens to share the badge's own citationKey.
    const findReferenceByLabel = (label) => {
      const messageBlock = props.messageData?.data?.[0] || {};
      const secondOrderReferences = Array.isArray(messageBlock.secondOrderReferences)
        ? messageBlock.secondOrderReferences
        : [];
      return secondOrderReferences.find((ref) => ref.index === label) || {};
    };

    const currentIndex = computed(
      () => (props.messageData?.data?.[0]?.references || []).findIndex((ref) => ref.externalId === props.id) + 1,
    );

    const title = computed(() => currentReference.value?.text || currentReference.value?.name || '');

    const snippet = computed(() => {
      if (!currentReference.value?.snippets) {
        return '';
      }
      const specificSnippet = currentReference.value.snippets[`${props.page}-${props.snippetIndex}`];
      return '...' + decodeFromBase64(specificSnippet || currentReference.value?.snippet || '');
    });

    const loadPageCover = async () => {
      thumbUrl.value = await props.onGetTooltipThumb(props.id, props.page);
    };

    const showTooltip = async () => {
      if (isOverflow.value) {
        overflowReferences.value = props.overflowEntries.map((entry) => ({
          ...findReferenceByLabel(entry.label),
          overflowLabel: entry.label,
        }));
      } else if (display2ndOrderReferences.value) {
        currentReference.value = findReference();
      } else {
        currentReference.value = (props.messageData?.data?.[0]?.references || []).find(
          (ref) => ref.externalId === props.id,
        );
      }
      computeTooltipStyle();
      isTooltipVisible.value = true;
      await loadPageCover();
    };

    const hideTooltip = () => (isTooltipVisible.value = false);
    // Fires from the root wrapper's own click and from clicking blank space inside the opened
    // tooltip (InfoTooltipContent's @title-clicked with no payload) — neither is a specific
    // reference when this badge represents an overflow ("+N") group, so there's nothing to open.
    // Individual overflow list items pass their own ref as refOverride and still open normally.
    const clickTitle = (refOverride) => {
      if (isOverflow.value && !refOverride) return;
      emit('titleClicked', { reference: refOverride || currentReference.value, page: Number(props.page) });
    };
    const clickPreview = (payload) => {
      // Overflow list items forward { reference, page } (which specific source was clicked);
      // the single-reference content just forwards a page number for currentReference.
      if (payload && typeof payload === 'object') {
        emit('titleClicked', { reference: payload.reference, page: Number(payload.page) });
      } else {
        emit('titleClicked', { reference: currentReference.value, page: Number(payload) });
      }
    };

    return {
      wrapperEl,
      tooltipStyle,
      currentIndex,
      currentReference,
      overflowReferences,
      isOverflow,
      displayLabel,
      clickPreview,
      clickTitle,
      thumbUrl,
      isTooltipVisible,
      showTooltip,
      hideTooltip,
      title,
      snippet,
      display2ndOrderReferences,
    };
  },
};
</script>

<style scoped lang="scss">
.main-wrapper {
  position: relative;

  .info-icon-black {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 16px;
    height: 16px;
    background-color: $bb-neutral-1;
    border-radius: 50%;
    color: $bb-neutral-0;
    box-sizing: border-box;
    margin-right: $bb-spacing-1;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;

    &:hover {
      background-color: $bb-primary-3;
    }
  }

  .tooltip-container {
    // Pure positioning wrapper only. This class lands on InfoTooltipContent's own root element
    // (its .container class, via attribute fallthrough) alongside InfoTooltipContent.vue's own
    // scoped sizing/appearance rules — declaring width/max-height/border/etc. here too meant two
    // different scoped stylesheets fighting over the same element with different values (this
    // component said 400px/432px, InfoTooltipContent said 300px/232px), an ambiguous conflict
    // resolved only by CSS source order. InfoTooltipContent.vue is the sole source of truth for
    // its own appearance now; this wrapper only positions it.
    //
    // Horizontal position is fully computed in TextMessageWithTooltip.vue's computeTooltipStyle
    // (clamped to stay within the message container) and passed in via the style binding — no
    // static left/transform here, since that would apply on top of the computed value and throw
    // the clamping off (this is exactly what caused left-edge truncation before).
    position: absolute;
    z-index: 1000;
  }
}
</style>
